import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ModalSheet.module.css';

interface ModalSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  actions?: Array<{
    text: string;
    variant?: 'primary' | 'secondary' | 'danger';
    onClick: () => void;
    disabled?: boolean;
  }>;
  showCloseButton?: boolean;
  fullScreen?: boolean;
}

const ModalSheet: React.FC<ModalSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  actions,
  showCloseButton = true,
  fullScreen = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
      
      // Handle Telegram WebApp back button
      if (window.Telegram?.WebApp) {
        const backButton = window.Telegram.WebApp.BackButton;
        backButton.show();
        backButton.onClick(onClose);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
      
      // Hide Telegram back button when modal closes
      if (window.Telegram?.WebApp) {
        const backButton = window.Telegram.WebApp.BackButton;
        backButton.hide();
        backButton.offClick(onClose);
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className={styles.modalBackdrop} onClick={fullScreen ? undefined : onClose} />
      <div className={styles.modalSheet} ref={modalRef}>
        {/* Handle bar */}
        <div className={styles.handleBar} />
        
        {/* Header */}
        {(title || showCloseButton) && (
          <div className={styles.modalHeader}>
            {title && <h2 className={styles.modalTitle}>{title}</h2>}
            {showCloseButton && (
              <button className={styles.closeButton} onClick={onClose}>
                ×
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className={styles.modalContent}>
          {children}
        </div>

        {/* Actions */}
        {actions && actions.length > 0 && (
          <div className={styles.modalActions}>
            {actions.map((action, index) => (
              <button
                key={index}
                className={`${styles.modalButton} ${styles[action.variant || 'secondary']}`}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default ModalSheet;

